'use client';

import ErrorMessage from '@/components/common/error-message/ErrorMessage';
import { Skeleton } from '@/components/ui/skeleton';
import useGetOrganization from '@/hooks/queries/organization/useGetOrganization';
import useGetServiceByIdOrCode from '@/hooks/queries/services/useGetServiceByIdOrCode';
import useGetUser from '@/hooks/queries/user/useGetUser';
import { useRootStore } from '@/providers/root-store-provider';
import { useUser } from '@auth0/nextjs-auth0/client';
import { useParams, useRouter } from 'next/navigation';
import { useEffect } from 'react';
import NBLandingByDivisionGroups from './NBLandingByDivisionGroups';
import NBLandingByDivisionGroupsWingfoil from './NBLandingByDivisionGroupsWingfoil';
import NBLandingContainer from './NBLandingContainer';

const NewBookingLanding = () => {
  const router = useRouter();
  const { organizationName, locale, serviceId } = useParams<{
    organizationName: string;
    locale: string;
    serviceId: string;
  }>();
  const { data: organization, isPending: isPendingOrganization } =
    useGetOrganization(organizationName);
  const organizationId = useRootStore((state) => state.organizationId);
  const setOrganizationId = useRootStore((store) => store.setOrganizationId);
  const { data: service, isPending: isPendingService } =
    useGetServiceByIdOrCode({
      serviceId,
      organizationCode: organizationName
    });

  // get user data, if is logged
  const { user } = useUser();
  const { data: userData, isLoading: isLoadingUserData } = useGetUser(
    user?.email
  );
  useEffect(() => {
    if (organization && organizationId !== organization.id) {
      setOrganizationId(organization.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  if (isPendingOrganization || isLoadingUserData || isPendingService) {
    return <Skeleton className="h-[600px] w-full" />;
  }

  if (!service || !organization) {
    if (!isPendingService) {
      router.push(`/${locale}/not-found`);
    }
    return <ErrorMessage />;
  }

  if (!service.showOnlyInLanding) {
    router.push(`/${locale}/${organizationName}/new-booking`);
  }

  // TODO: remove this condition when the service code is not needed with dynamic forms
  if (
    service.code === 'evento-wingfoil' ||
    service.code === 'wingfoil-edicion-mujeres' ||
    service.code === 'circuito-autonomico-wingfoil' ||
    service.code === 'wingfoil-edicion-mujeres'
  ) {
    return (
      <div className="mt-8 mb-14 md:container">
        <NBLandingByDivisionGroupsWingfoil
          organization={organization}
          user={userData}
          service={service}
        />
      </div>
    );
  }

  if (service.code === 'campus-nautico-verano') {
    return (
      <div className="mt-8 mb-14 md:container">
        <NBLandingByDivisionGroups
          organization={organization}
          user={userData}
          service={service}
        />
      </div>
    );
  }

  return (
    <NBLandingContainer
      service={service}
      organization={organization}
      user={userData}
    />
  );
};

export default NewBookingLanding;
